import { create } from "zustand";
import {
  arenaApi,
  type ArenaShopItem,
  type PartyRoomDetail,
  type PartyWindow,
} from "../api/arena";
import { useBattleStore } from "./useBattleStore";
import { useGameStore } from "./useGameStore";

function isPartyRematchPendingForViewer(
  room: PartyRoomDetail | null | undefined,
  viewerUserId: number | undefined,
): boolean {
  if (!room || room.status !== "started") return false;
  if (viewerUserId == null) return false;
  return room.leader_id !== viewerUserId;
}

function partyRewardPatch(me: {
  party_reward_attempts_used?: number;
  party_reward_attempts_limit?: number;
  party_rewards_exhausted?: boolean;
}) {
  return {
    partyRewardAttemptsUsed: me.party_reward_attempts_used ?? 0,
    partyRewardAttemptsLimit: me.party_reward_attempts_limit ?? 30,
    partyRewardsExhausted: me.party_rewards_exhausted ?? false,
  };
}

interface ArenaRankEntry {
  rank: number;
  user_id: number | null;
  name: string;
  br: number;
  class: string;
  level: number;
  is_npc: boolean;
  can_fight: boolean;
  fight_block_reason: string | null;
}

interface SoloArenaRules {
  rank_attack_above: number;
  rank_attack_below: number;
  unranked_attack_bottom: number;
}

interface ArenaStore {
  soloRanks: ArenaRankEntry[];
  myRank: number | null;
  soloRules: SoloArenaRules | null;
  dailyAttempts: number;
  lastAttack: string | null;
  skipCooldownDiamonds: number;
  vipSkipFree: boolean;
  soloCooldownExempt: boolean;
  attemptCooldownSec: number;
  scheduleOpen: boolean;
  partyWindows: PartyWindow[];
  scheduleWindowEnd: string | null;
  scheduleNextStart: string | null;
  adminOverride: boolean;
  myPartyRoom: PartyRoomDetail | null;
  partyRooms: PartyRoomDetail[];
  partyMatchmaking: boolean;
  partyRematchPending: boolean;
  partyExpiredModal: string | null;
  partyBattleLaunching: boolean;
  partyBattleSeenId: number | null;
  partyRewardAttemptsUsed: number;
  partyRewardAttemptsLimit: number;
  partyRewardsExhausted: boolean;
  arenaTab: "solo" | "party" | "shop";
  shopItems: ArenaShopItem[];
  leagueTier: string;
  leaguePoints: number;
  loading: boolean;
  error: string | null;

  fetchSoloRanks: () => Promise<void>;
  attackSolo: (targetRank: number, mode: string) => Promise<void>;
  skipCooldown: () => Promise<void>;
  fetchSchedule: () => Promise<void>;
  refreshPartyRooms: () => Promise<void>;
  pollParty: () => Promise<void>;
  createPartyRoom: () => Promise<void>;
  joinPartyRoom: (roomId: string) => Promise<void>;
  leavePartyRoom: () => Promise<void>;
  togglePartyReady: () => Promise<void>;
  startPartyFight: () => Promise<void>;
  cancelPartyCountdown: () => Promise<void>;
  ackPartyBattle: () => Promise<void>;
  leaveCurrentPartyRoom: () => Promise<void>;
  dismissPartyExpiredModal: () => void;
  setArenaTab: (tab: "solo" | "party" | "shop") => void;
  fetchShop: () => Promise<void>;
  buyItem: (itemId: string, quantity: number) => Promise<void>;
  fetchLeagueStatus: () => Promise<void>;
}

export const useArenaStore = create<ArenaStore>((set, get) => ({
  soloRanks: [],
  myRank: null,
  soloRules: null,
  dailyAttempts: 0,
  lastAttack: null,
  skipCooldownDiamonds: 10,
  vipSkipFree: false,
  soloCooldownExempt: false,
  attemptCooldownSec: 600,
  scheduleOpen: false,
  partyWindows: [],
  scheduleWindowEnd: null,
  scheduleNextStart: null,
  adminOverride: false,
  myPartyRoom: null,
  partyRooms: [],
  partyMatchmaking: false,
  partyRematchPending: false,
  partyExpiredModal: null,
  partyBattleLaunching: false,
  partyBattleSeenId: null,
  partyRewardAttemptsUsed: 0,
  partyRewardAttemptsLimit: 30,
  partyRewardsExhausted: false,
  arenaTab: "solo",
  shopItems: [],
  leagueTier: "NONE",
  leaguePoints: 0,
  loading: false,
  error: null,

  fetchSoloRanks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await arenaApi.getSoloRanks();
      set({
        soloRanks: data.ranks,
        myRank: data.my_rank,
        soloRules: data.rules,
        dailyAttempts: data.daily_attempts,
        lastAttack: data.last_attack,
        skipCooldownDiamonds: data.skip_cooldown_diamonds,
        vipSkipFree: data.vip_skip_free ?? false,
        soloCooldownExempt: data.solo_cooldown_exempt ?? false,
        attemptCooldownSec: data.attempt_cooldown_sec ?? 600,
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to fetch ranks" });
    } finally {
      set({ loading: false });
    }
  },

  attackSolo: async (targetRank: number, mode: string) => {
    set({ loading: true, error: null });
    try {
      const result = await arenaApi.attackSolo(targetRank, mode);
      const loaded = await useBattleStore.getState().loadBattle(result.battle_id, result.info_messages);
      if (!loaded) {
        const battleError = useBattleStore.getState().error;
        set({ error: battleError ?? "Failed to load battle replay" });
        return;
      }
      useGameStore.getState().setScreen("battle");
      await get().fetchSoloRanks();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Attack failed" });
    } finally {
      set({ loading: false });
    }
  },

  skipCooldown: async () => {
    set({ loading: true, error: null });
    try {
      await arenaApi.skipCooldown();
      await get().fetchSoloRanks();
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to skip cooldown" });
    } finally {
      set({ loading: false });
    }
  },

  fetchSchedule: async () => {
    try {
      const data = await arenaApi.getSchedule();
      set({
        scheduleOpen: data.is_open,
        partyWindows: data.windows ?? [],
        scheduleWindowEnd: data.current_window_end ?? null,
        scheduleNextStart: data.next_window_start ?? null,
        adminOverride: data.admin_override ?? false,
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to fetch schedule" });
    }
  },

  refreshPartyRooms: async () => {
    try {
      const data = await arenaApi.getPartyRooms();
      set({ partyRooms: data.rooms });
    } catch {
      // non-fatal during polling
    }
  },

  pollParty: async () => {
    const { partyBattleLaunching, partyBattleSeenId } = get();
    if (partyBattleLaunching) return;

    try {
      const me = await arenaApi.getPartyMe();
      const game = useGameStore.getState();
      const battle = useBattleStore.getState();
      const viewerUserId = game.user?.id;
      const rewards = partyRewardPatch(me);

      if (me.status === "expired") {
        set({
          myPartyRoom: null,
          partyMatchmaking: false,
          partyExpiredModal: me.expired_message ?? "Party room closed.",
          ...rewards,
        });
        return;
      }

      if (me.status === "started" && me.battle_id) {
        if (partyBattleSeenId === me.battle_id) {
          set({
            myPartyRoom: me.room ?? null,
            partyMatchmaking: false,
            partyRematchPending: isPartyRematchPendingForViewer(me.room, viewerUserId),
            ...rewards,
          });
          return;
        }
        if (battle.phase !== "idle" && battle.battle?.id === me.battle_id) {
          return;
        }
        set({
          partyBattleLaunching: true,
          partyMatchmaking: false,
          partyBattleSeenId: me.battle_id,
          myPartyRoom: me.room ?? get().myPartyRoom,
        });
        const loaded = await useBattleStore.getState().loadBattle(
          me.battle_id,
          me.info_messages ?? [],
        );
        if (loaded) {
          get().setArenaTab("party");
          game.setScreen("battle");
        } else {
          set({
            error: useBattleStore.getState().error ?? "Failed to load party battle",
          });
        }
        set({ partyBattleLaunching: false });
        return;
      }

      if (me.status === "countdown" && me.room) {
        set({
          myPartyRoom: me.room,
          partyMatchmaking: false,
          partyRematchPending: false,
          partyExpiredModal: null,
          ...rewards,
        });
        return;
      }

      if (me.status === "spectating" && me.room) {
        set({
          myPartyRoom: me.room,
          partyMatchmaking: false,
          partyRematchPending: isPartyRematchPendingForViewer(me.room, viewerUserId),
          partyExpiredModal: null,
          ...rewards,
        });
        return;
      }

      if (me.status === "matchmaking") {
        set({
          myPartyRoom: me.room ?? null,
          partyMatchmaking: true,
          partyExpiredModal: null,
          ...rewards,
        });
        return;
      }

      if (me.status === "rematch_pending" && me.room) {
        set({
          myPartyRoom: me.room,
          partyMatchmaking: false,
          partyRematchPending: isPartyRematchPendingForViewer(me.room, viewerUserId),
          partyExpiredModal: null,
          ...rewards,
        });
        return;
      }

      if (me.status === "waiting" && me.room) {
        set({
          myPartyRoom: me.room,
          partyMatchmaking: false,
          partyRematchPending: false,
          partyExpiredModal: null,
          ...rewards,
        });
        return;
      }

      set({ myPartyRoom: null, partyMatchmaking: false, ...rewards });
    } catch {
      // ignore transient poll errors
    }
  },

  createPartyRoom: async () => {
    set({ loading: true, error: null });
    try {
      const data = await arenaApi.createPartyRoom();
      if (data.room) {
        set({ myPartyRoom: data.room });
      } else {
        await get().pollParty();
      }
      await get().refreshPartyRooms();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create room" });
    } finally {
      set({ loading: false });
    }
  },

  joinPartyRoom: async (roomId: string) => {
    set({ loading: true, error: null });
    try {
      const data = await arenaApi.joinPartyRoom(roomId);
      set({ myPartyRoom: data.room });
      await get().refreshPartyRooms();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to join room" });
    } finally {
      set({ loading: false });
    }
  },

  leavePartyRoom: async () => {
    set({ loading: true, error: null });
    try {
      await arenaApi.leaveCurrentPartyRoom();
      set({ myPartyRoom: null, partyMatchmaking: false });
      await get().refreshPartyRooms();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to leave room" });
    } finally {
      set({ loading: false });
    }
  },

  togglePartyReady: async () => {
    const { myPartyRoom } = get();
    if (!myPartyRoom) return;
    set({ loading: true, error: null });
    try {
      const data = await arenaApi.toggleReady(myPartyRoom.room_id);
      set({ myPartyRoom: data.room });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to toggle ready" });
    } finally {
      set({ loading: false });
    }
  },

  startPartyFight: async () => {
    const { myPartyRoom } = get();
    if (!myPartyRoom) return;
    set({ loading: true, error: null });
    try {
      const result = await arenaApi.startFight(myPartyRoom.room_id);
      if (result.status === "countdown" && result.room) {
        set({ myPartyRoom: result.room, partyMatchmaking: false });
        return;
      }
      if (result.status === "started" && result.battle_id) {
        set({ partyBattleSeenId: result.battle_id, myPartyRoom: result.room ?? myPartyRoom });
        const loaded = await useBattleStore.getState().loadBattle(
          result.battle_id,
          result.info_messages ?? [],
        );
        if (loaded) {
          get().setArenaTab("party");
          useGameStore.getState().setScreen("battle");
        } else {
          set({
            error: useBattleStore.getState().error ?? "Failed to load party battle",
          });
        }
      } else if (result.status === "matchmaking" && result.room) {
        set({ myPartyRoom: result.room, partyMatchmaking: true });
      } else {
        await get().pollParty();
      }
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : "Failed to start fight",
        partyMatchmaking: false,
      });
    } finally {
      set({ loading: false });
    }
  },

  cancelPartyCountdown: async () => {
    const { myPartyRoom } = get();
    if (!myPartyRoom) return;
    set({ loading: true, error: null });
    try {
      const data = await arenaApi.cancelCountdown(myPartyRoom.room_id);
      if (data.room) {
        set({ myPartyRoom: data.room, partyMatchmaking: false });
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to cancel countdown" });
    } finally {
      set({ loading: false });
    }
  },

  dismissPartyExpiredModal: () => set({ partyExpiredModal: null }),

  ackPartyBattle: async () => {
    try {
      const res = await arenaApi.ackPartyBattle();
      if (res.status === "waiting" && res.room) {
        set({ myPartyRoom: res.room, partyMatchmaking: false, partyRematchPending: false });
      } else if (res.status === "spectating" && res.room) {
        const viewerUserId = useGameStore.getState().user?.id;
        set({
          myPartyRoom: res.room,
          partyMatchmaking: false,
          partyRematchPending: isPartyRematchPendingForViewer(res.room, viewerUserId),
        });
      } else if (res.room) {
        set({ myPartyRoom: res.room, partyMatchmaking: false });
      }
    } catch {
      // non-fatal
    }
  },

  leaveCurrentPartyRoom: async () => get().leavePartyRoom(),

  setArenaTab: (tab) => set({ arenaTab: tab }),

  fetchShop: async () => {
    try {
      const data = await arenaApi.getShop();
      set({ shopItems: data });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to fetch shop" });
    }
  },

  buyItem: async (itemId: string, quantity: number) => {
    try {
      await arenaApi.buyItem(itemId, quantity);
      await useGameStore.getState().refreshBalance();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Purchase failed" });
    }
  },

  fetchLeagueStatus: async () => {
    try {
      const data = await arenaApi.getLeagueStatus();
      set({ leagueTier: data.tier, leaguePoints: data.points });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to fetch league status" });
    }
  },
}));
